import React, { useState } from 'react';
import QRCodeModal from './QRCodeModal';
import RatingWidget from './RatingWidget';
import VPNInstructionsModal from './VPNInstructionsModal';
import chatService from '../services/chatService';
import {
  shareToTelegram,
  sendToTelegramBot,
  shareViaWebAPI,
  saveSubscriptionToFile,
  isWebShareSupported
} from '../utils/shareUtils';
import { openInRecommendedApp, getRecommendedApps } from '../utils/deepLinks';

const ResultCard = ({ result, onNewOrder, showToast }) => {
  const [copied, setCopied] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (showToast) {
        showToast('Подписка скопирована в буфер обмена!', 'success');
      }
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      if (showToast) {
        showToast('Не удалось скопировать', 'error');
      }
    }
  };

  const handleShareToTelegram = () => {
    shareToTelegram('Моя VLESS подписка', result.subscription);
    if (showToast) {
      showToast('Открываем Telegram...', 'info');
    }
  };

  const handleSendToBot = () => {
    sendToTelegramBot(result.subscription);
    if (showToast) {
      showToast('Открываем Telegram бот...', 'info');
    }
  };

  const handleShareViaWeb = async () => {
    const shared = await shareViaWebAPI(
      'VLESS Подписка',
      'Моя восстановленная подписка',
      result.subscription
    );
    if (!shared) {
      // Fallback на Telegram если Web Share API не поддерживается
      handleShareToTelegram();
    }
  };

  const handleSaveToFile = () => {
    saveSubscriptionToFile(result.subscription, result.orderNumber);
    if (showToast) {
      showToast('Файл сохранен!', 'success');
    }
  };

  const handleOpenInApp = () => {
    const opened = openInRecommendedApp(result.subscription, showToast);
    if (opened && showToast) {
      showToast('Открываем в VPN приложении...', 'info');
    }
  };

  const recommendedApps = getRecommendedApps();

  const handleRate = async (rating, feedback, clientIP) => {
    try {
      await chatService.submitRating(rating, feedback, clientIP);
      return true;
    } catch (error) {
      console.error('Ошибка отправки рейтинга:', error);
      return false;
    }
  };

  const isAlreadyRestored = result.isAlreadyRestored;
  const restoredDate = isAlreadyRestored && result.restoredAt 
    ? new Date(result.restoredAt).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : null;

  return (
    <div className="card p-6 md:p-8">
      <div className="text-center mb-6 md:mb-8">
        <div className={`w-14 h-14 md:w-16 md:h-16 ${isAlreadyRestored ? 'bg-blue-100' : 'bg-success-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
          {isAlreadyRestored ? (
            <svg className="w-7 h-7 md:w-8 md:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-7 h-7 md:w-8 md:h-8 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
          {isAlreadyRestored ? 'Подписка найдена в истории!' : 'Подписка успешно восстановлена!'}
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          {isAlreadyRestored 
            ? `Этот заказ был восстановлен ${restoredDate}` 
            : 'Ваша новая подписка готова к использованию'}
        </p>
        

        {/* Подписка - выделенный блок */}
        <div className="mt-4 p-3 md:p-5 bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 gap-2">
            <label className="text-base md:text-xl font-bold text-primary-800 flex items-center">
              <svg className="w-5 h-5 md:w-6 md:h-6 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Ваша подписка
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowQRCode(true)}
                className="flex items-center px-2 md:px-3 py-1.5 text-xs md:text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                title="Показать QR код"
              >
                <svg className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                <span className="hidden sm:inline">QR код</span>
                <span className="sm:hidden">QR</span>
              </button>
              <button
                onClick={() => copyToClipboard(result.subscription)}
                className="flex items-center px-2 md:px-3 py-1.5 text-xs md:text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                title="Копировать подписку"
              >
                {copied ? (
                  <>
                    <svg className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="hidden sm:inline">Скопировано</span>
                    <span className="sm:hidden">✓</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="hidden sm:inline">Копировать</span>
                    <span className="sm:hidden">Копир</span>
                  </>
                )}
              </button>
            </div>
          </div>
        
          <div className="relative">
            <textarea
              value={result.subscription}
              readOnly
              className="w-full px-3 md:px-4 py-2 md:py-3 bg-white border-2 border-primary-200 rounded-lg font-mono text-xs md:text-base resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={2}
            />
          </div>
          
          {copied && (
            <div className="mt-3 p-2 md:p-3 bg-success-50 border border-success-200 rounded-lg">
              <p className="text-xs md:text-sm text-success-700 flex items-center">
                <svg className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="hidden sm:inline">Подписка успешно скопирована в буфер обмена</span>
                <span className="sm:hidden">Скопировано!</span>
              </p>
            </div>
          )}
        </div>

        {/* Инструкции - на мобильных показываем сразу, на десктопе после эмоционального блока */}
        <div className="mt-3 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-600 mt-0.5 mr-2 md:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm text-blue-700 leading-relaxed">
                <strong>Это подписка!</strong> Скопируйте всю ссылку и добавьте в ваше VPN приложение (Happ, Nekoray, Hiddify, Streisand и т.д.)
              </p>
            </div>
          </div>
        </div>

        {/* Компактный эмоциональный блок - только для новых подписок и только на десктопе */}
        {!isAlreadyRestored && (
          <div className="hidden md:block mt-4 p-3 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-green-800 leading-relaxed">
                  <strong>🎉 Вы получили больше, чем потеряли!</strong> Было: обычный ключ с одной локацией → Стало: премиум подписка с <strong>10+ локациями</strong> и <strong>15+ серверами</strong>
                </p>
                <p className="text-xs text-green-600 mt-1">
                  💡 Хотите еще больше? Оформите дополнительную подписку или посоветуйте другу и получите бонусы в <strong>@vless_helperz_bot</strong>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

        {/* Уведомление об отправке на email */}
        {result.emailSent && !isAlreadyRestored && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg animate-fade-in-up">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-green-900 mb-1">
                  📧 Подписка отправлена на email!
                </h3>
                <p className="text-sm text-green-700">
                  Мы сохранили вашу подписку и в скором времени отправим её на указанный email адрес. Проверьте почту через несколько минут.
                </p>
              </div>
            </div>
          </div>
        )}

        {isAlreadyRestored && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-blue-900 mb-1">
                Заказ уже был восстановлен
              </h3>
              <p className="text-sm text-blue-700">
                Используется ранее сгенерированная подписка. Если вам нужна новая подписку, обратитесь в поддержку.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Bot Ad */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white">
        <div className="flex items-start">
          <svg className="w-6 h-6 text-white mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white mb-1 leading-tight">
              🚀 Управляйте подпиской через @vless_helperz_bot
            </h3>
            <p className="text-sm text-blue-100 leading-relaxed mb-3">
              Получите статистику, реферальные бонусы, промокоды и скидки!
            </p>
            <a
              href="https://t.me/vless_helperz_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              Привязать подписку
            </a>
          </div>
        </div>
      </div>

      <div className="space-y-4 md:space-y-6">
        {/* Информация о заказе */}
        <div className="bg-gray-50 rounded-lg p-4 md:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-2 sm:mb-0">
              Информация о заказе
            </h3>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-xs md:text-sm text-gray-600 font-mono break-all">
                #{result.orderNumber}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs md:text-sm font-medium bg-success-100 text-success-800 w-fit">
                {result.status}
              </span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center">
            <span className="text-sm text-gray-600 mb-1 sm:mb-0 sm:mr-2">Дата заказа:</span>
            <span className="text-sm md:text-base font-semibold text-gray-800">{result.orderDate}</span>
          </div>
        </div>


        {/* Инструкции */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-5">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm md:text-base font-semibold text-blue-900 mb-2">
                Инструкция по использованию
              </h3>
              <div className="text-sm text-blue-700 space-y-1">
                <p>1. Скопируйте VLESS ключ выше</p>
                <p>2. Вставьте его в настройки вашего VPN клиента</p>
                <p>3. Старый ключ автоматически деактивируется</p>
              </div>
            </div>
          </div>
        </div>

        {/* Виджет рейтинга */}
        <RatingWidget onRate={handleRate} />

        {/* Дополнительные действия */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Быстрые действия
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {/* Открыть в приложении - если есть рекомендованные приложения */}
            {recommendedApps.length > 0 && (
              <button
                onClick={handleOpenInApp}
                className="flex flex-col items-center p-3 bg-white hover:bg-gradient-to-br hover:from-green-50 hover:to-blue-50 border border-gray-200 hover:border-green-300 rounded-lg transition-all text-center shadow-sm hover:shadow-md"
              >
                <span className="text-2xl mb-1">{recommendedApps[0].icon}</span>
                <span className="text-xs font-medium text-gray-700">Открыть в {recommendedApps[0].name}</span>
              </button>
            )}
            <button
              onClick={() => setShowInstructions(true)}
              className="flex flex-col items-center p-3 bg-white hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg transition-all text-center"
            >
              <svg className="w-6 h-6 text-blue-600 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-xs font-medium text-gray-700">Инструкции</span>
            </button>

            <button
              onClick={handleSaveToFile}
              className="flex flex-col items-center p-3 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-300 rounded-lg transition-all text-center"
            >
              <svg className="w-6 h-6 text-green-600 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              <span className="text-xs font-medium text-gray-700">Сохранить</span>
            </button>

            {isWebShareSupported() ? (
              <button
                onClick={handleShareViaWeb}
                className="flex flex-col items-center p-3 bg-white hover:bg-purple-50 border border-gray-200 hover:border-purple-300 rounded-lg transition-all text-center"
              >
                <svg className="w-6 h-6 text-purple-600 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span className="text-xs font-medium text-gray-700">Поделиться</span>
              </button>
            ) : (
              <button
                onClick={handleShareToTelegram}
                className="flex flex-col items-center p-3 bg-white hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg transition-all text-center"
              >
                <svg className="w-6 h-6 text-blue-600 mb-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                <span className="text-xs font-medium text-gray-700">Telegram</span>
              </button>
            )}

            <button
              onClick={handleSendToBot}
              className="flex flex-col items-center p-3 bg-white hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 rounded-lg transition-all text-center"
            >
              <svg className="w-6 h-6 text-indigo-600 mb-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span className="text-xs font-medium text-gray-700">В бот</span>
            </button>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            onClick={() => copyToClipboard(result.subscription)}
            className="btn-primary flex-1 text-sm md:text-base"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Копировать подписку
          </button>
          <button
            onClick={onNewOrder}
            className="btn-secondary flex-1 text-sm md:text-base"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Новый заказ
          </button>
        </div>
      </div>
      
      <QRCodeModal 
        isOpen={showQRCode}
        onClose={() => setShowQRCode(false)}
        subscription={result.subscription}
        orderNumber={result.orderNumber}
      />
      
      <VPNInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
      />
    </div>
  );
};

export default ResultCard;
